import { sanitizeCompanySlug } from '../utils/companySlug'
import type { GateOption } from './gates'

export type CompanyTheme = {
  primary: string
  primaryContrast: string
  accent: string
  accentSoft: string
  sidebar: string
  header: string
  surface: string
  surfaceAlt: string
  text: string
  textMuted: string
}

export type CompanyProfile = {
  slug: string
  brandName: string
  logoUrl?: string
  tenantId?: string
  dashboardApiBaseUrl: string
  userApiBaseUrl: string
  eventApiBaseUrl: string
  ticketSearchPath: string
  ticketSearchWithSessionsPath: string
  enforceGateValidation?: boolean
  portalOrigin?: string
  companyId?: string
  staticGates?: GateOption[]
  theme: CompanyTheme
}

const DEFAULT_THEME: CompanyTheme = {
  primary: '#121926',
  primaryContrast: '#ffffff',
  accent: '#1f6feb',
  accentSoft: 'rgba(31, 111, 235, 0.18)',
  sidebar: '#101826',
  header: '#0f1724',
  surface: '#ffffff',
  surfaceAlt: '#f4f7fb',
  text: '#172033',
  textMuted: '#5b6577'
}

const buildProfile = (
  profile: Omit<Partial<CompanyProfile>, 'theme'> & {
    slug: string
    theme?: Partial<CompanyTheme>
  }
): CompanyProfile => ({
  slug: profile.slug,
  brandName: profile.brandName || 'BePass',
  logoUrl: profile.logoUrl,
  dashboardApiBaseUrl:
    profile.dashboardApiBaseUrl ||
    'https://botafogo-dashboard-api-74hf2txj6q-ue.a.run.app',
  userApiBaseUrl: profile.userApiBaseUrl || 'https://users.bepass.app',
  eventApiBaseUrl: profile.eventApiBaseUrl || 'https://tickets.bepass.app',
  ticketSearchPath: profile.ticketSearchPath || '/tickets/search',
  ticketSearchWithSessionsPath:
    profile.ticketSearchWithSessionsPath || '/tickets/search/withsessions',
  enforceGateValidation: profile.enforceGateValidation || false,
  portalOrigin:
    profile.portalOrigin || `https://app-${profile.slug}.bepass.com.br`,
  companyId: profile.companyId,
  staticGates: profile.staticGates,
  theme: {
    ...DEFAULT_THEME,
    ...profile.theme
  }
})

const COMPANY_PROFILES: Record<string, CompanyProfile> = {
  default: buildProfile({
    slug: 'default',
    brandName: 'BePass',
    portalOrigin: 'https://app.bepass.com.br',
    theme: {
      primary: '#0f172a',
      accent: '#2563eb',
      accentSoft: 'rgba(37, 99, 235, 0.16)',
      sidebar: '#0b1220',
      header: '#0f172a'
    }
  }),
  botafogo: buildProfile({
    slug: 'botafogo',
    brandName: 'Botafogo',
    logoUrl:
      'https://storage.googleapis.com/companies-assets/botafogo/client_logo.58472699ee7531a3cacb.png',
    dashboardApiBaseUrl: 'https://botafogo-dashboard-api-74hf2txj6q-ue.a.run.app',
    userApiBaseUrl: 'https://users.bepass.app',
    eventApiBaseUrl: 'https://tickets.bepass.app',
    companyId: '6e7fc4d2-2b5e-48b9-acad-da23b5011b26',
    theme: {
      primary: '#ba8a4d',
      primaryContrast: '#ffffff',
      accent: '#000000',
      accentSoft: 'rgba(186, 138, 77, 0.18)',
      sidebar: '#000000',
      header: '#000000',
      surfaceAlt: '#eef1f4'
    }
  }),
  fluminense: buildProfile({
    slug: 'fluminense',
    brandName: 'Fluminense',
    logoUrl:
      'https://storage.googleapis.com/companies-assets/fluminense/fluminense_2023619115226.png',
    dashboardApiBaseUrl: 'https://fluminense-dashboard-api-74hf2txj6q-ue.a.run.app',
    userApiBaseUrl: 'https://users.bepass.app',
    eventApiBaseUrl: 'https://tickets.bepass.app',
    ticketSearchPath: '/tickets/search/legacy',
    companyId: '2c25d521-193c-4237-b80c-d1675335366f',
    theme: {
      primary: '#6f1d39',
      accent: '#1d8f68',
      accentSoft: 'rgba(29, 143, 104, 0.18)',
      sidebar: '#4d1327',
      header: '#6f1d39',
      surfaceAlt: '#f8f1f4'
    }
  }),
  gremio: buildProfile({
    slug: 'gremio',
    brandName: 'Gremio',
    logoUrl:
      'https://storage.googleapis.com/companies-assets/arena-gremio/gremio.png',
    dashboardApiBaseUrl:
      'https://gremio-dashboard-api-74hf2txj6q-rj.a.run.app',
    userApiBaseUrl: 'https://users.bepass.app',
    eventApiBaseUrl: 'https://tickets.bepass.app',
    ticketSearchPath: '/tickets/search/legacy',
    companyId: 'e5905d4c-b0fd-43e7-8586-644772f812ae',
    theme: {
      primary: '#1f6aa5',
      accent: '#0d0d0d',
      accentSoft: 'rgba(31, 106, 165, 0.18)',
      sidebar: '#0f2b45',
      header: '#174d77',
      surfaceAlt: '#eef6fb'
    }
  }),
  palmeiras: buildProfile({
    slug: 'palmeiras',
    brandName: 'Palmeiras',
    logoUrl:
      'https://storage.googleapis.com/companies-assets/palmeiras/logo_palmeiras.svg',
    dashboardApiBaseUrl: 'https://dashboard-api-74hf2txj6q-ue.a.run.app',
    userApiBaseUrl: 'https://users.bepass.app',
    eventApiBaseUrl: 'https://tickets.bepass.app',
    ticketSearchPath: '/tickets/search/legacy',
    staticGates: [
      { id: '', label: 'Portão: A Central Oeste',            integration_code: 'A Central Oeste',          alias: 'Portão: A Central Oeste' },
      { id: '', label: 'Portão: A Superior Sul',             integration_code: 'A Superior Sul',            alias: 'Portão: A Superior Sul' },
      { id: '', label: 'Portão: A Gol Sul',                  integration_code: 'A Gol Sul',                 alias: 'Portão: A Gol Sul' },
      { id: '', label: 'Portão: B Superior Leste',           integration_code: 'B Superior Leste',          alias: 'Portão: B Superior Leste' },
      { id: '', label: 'Portão: B Gol Norte',                integration_code: 'B Gol Norte',               alias: 'Portão: B Gol Norte' },
      { id: '', label: 'Portão: B Geral Norte',              integration_code: 'B Geral Norte',             alias: 'Portão: B Geral Norte' },
      { id: '', label: 'Portão: C Central Leste',            integration_code: 'C Central Leste',           alias: 'Portão: C Central Leste' },
      { id: '', label: 'Portão: D Visitantes',               integration_code: 'D Visitantes',              alias: 'Portão: D Visitantes' },
      { id: '', label: 'Portão: Parque Mirante 6 Andar - E7', integration_code: 'Parque Mirante 6 Andar - E7', alias: 'Portão: Parque Mirante 6 Andar - E7' },
      { id: '', label: 'Portão: Camarote 3 Andar - E3',     integration_code: 'Camarote 3 Andar - E3',    alias: 'Portão: Camarote 3 Andar - E3' },
      { id: '', label: 'Portão: Camarote 4+3 Andar',        integration_code: 'Camarote 4+3 Andar',       alias: 'Portão: Camarote 4+3 Andar' },
      { id: '', label: 'Portão: Camarote 4 Andar - E4',     integration_code: 'Camarote 4 Andar - E4',    alias: 'Portão: Camarote 4 Andar - E4' },
      { id: '', label: 'Portão: AVVIP',                      integration_code: 'AVVIP',                     alias: 'Portão: AVVIP' },
    ],
    theme: {
      primary: '#0d5f39',
      accent: '#d4b06a',
      accentSoft: 'rgba(212, 176, 106, 0.2)',
      sidebar: '#0a472b',
      header: '#0d5f39',
      surfaceAlt: '#eef8f2'
    }
  }),
  santos: buildProfile({
    slug: 'santos',
    brandName: 'Santos',
    logoUrl:
      'https://storage.googleapis.com/companies-assets/santos-fc/santos-fc_20231010152459.png',
    dashboardApiBaseUrl:
      'https://santos-dashboard-api-prod-74hf2txj6q-rj.a.run.app',
    userApiBaseUrl: 'https://users.bepass.app',
    eventApiBaseUrl: 'https://tickets.bepass.app',
    ticketSearchPath: '/tickets/search/legacy',
    theme: {
      primary: '#111827',
      accent: '#c0c7d1',
      accentSoft: 'rgba(192, 199, 209, 0.18)',
      sidebar: '#0b1220',
      header: '#111827',
      surfaceAlt: '#f4f6f8'
    }
  }),
  flamengo: buildProfile({
    slug: 'flamengo',
    brandName: 'Flamengo',
    logoUrl:
      'https://storage.googleapis.com/companies-assets/flamengo/flamengo_202344144837.png',
    dashboardApiBaseUrl: 'https://flamengo-dashboard-api-74hf2txj6q-ue.a.run.app',
    userApiBaseUrl: 'https://users.bepass.app',
    eventApiBaseUrl: 'https://tickets.bepass.app',
    ticketSearchPath: '/tickets/search/legacy',
    companyId: 'aac3a061-7d13-45ee-a8b3-47338bf976a9',
    theme: {
      primary: '#8f1111',
      accent: '#111111',
      accentSoft: 'rgba(17, 17, 17, 0.16)',
      sidebar: '#5f0b0b',
      header: '#8f1111',
      surfaceAlt: '#fbefef'
    }
  }),
  'sao-paulo': buildProfile({
    slug: 'sao-paulo',
    brandName: 'Sao Paulo',
    logoUrl:
      'https://storage.googleapis.com/companies-assets/sao-paulo-fc/sao-paulo-fc_20231111182624.png',
    dashboardApiBaseUrl: 'https://sao-paulo-dashboard-api-74hf2txj6q-ue.a.run.app',
    userApiBaseUrl: 'https://users.bepass.app',
    eventApiBaseUrl: 'https://tickets.bepass.app',
    companyId: '0309214a-b846-48d7-97da-d506ac044059',
    theme: {
      primary: '#111111',
      accent: '#d92027',
      accentSoft: 'rgba(217, 32, 39, 0.16)',
      sidebar: '#111111',
      header: '#242424',
      surfaceAlt: '#faf3f3'
    }
  }),
  vasco: buildProfile({
    slug: 'vasco',
    brandName: 'Vasco',
    logoUrl:
      'https://storage.googleapis.com/companies-assets/vasco/vasco_2023721135356.png',
    dashboardApiBaseUrl: 'https://vasco-dashboard-api-74hf2txj6q-ue.a.run.app',
    userApiBaseUrl: 'https://users.bepass.app',
    eventApiBaseUrl: 'https://tickets.bepass.app',
    companyId: '2c85d5a1-0fb3-4fbe-ade7-a3be09c8fcaa',
    theme: {
      primary: '#111111',
      accent: '#ffffff',
      accentSoft: 'rgba(255, 255, 255, 0.14)',
      sidebar: '#161616',
      header: '#111111'
    }
  }),
  barueri: buildProfile({
    slug: 'barueri',
    brandName: 'Barueri',
    logoUrl:
      'https://storage.cloud.google.com/companies-assets/barueri/logo-barueri.png',
    dashboardApiBaseUrl: 'https://barueri-dashboard-api-74hf2txj6q-ue.a.run.app',
    userApiBaseUrl: 'https://users.bepass.app',
    eventApiBaseUrl: 'https://tickets.bepass.app',
    ticketSearchPath: '/tickets/search/legacy',
    companyId: '53eb83ac-1213-48f8-8f2e-8c7f2f83405c',
    theme: {
      primary: '#155eef',
      accent: '#f59e0b',
      accentSoft: 'rgba(245, 158, 11, 0.18)',
      sidebar: '#12326b',
      header: '#155eef'
    }
  }),
  cbf: buildProfile({
    slug: 'cbf',
    brandName: 'CBF',
    logoUrl: '/cbf-logo.png',
    tenantId: 'cbf-game-govii',
    dashboardApiBaseUrl: 'https://botafogo-dashboard-api-74hf2txj6q-ue.a.run.app',
    userApiBaseUrl: 'https://users.bepass.app',
    eventApiBaseUrl: 'https://tickets.bepass.app',
    companyId: 'b8d679f2-3b8d-4b3e-9216-12e9363600c3',
    theme: {
      primary: '#014dff',
      primaryContrast: '#ffffff',
      accent: '#ffdf00',
      accentSoft: 'rgba(255, 223, 0, 0.18)',
      sidebar: '#012d99',
      header: '#013dcc',
      surfaceAlt: '#f3f7ff',
      text: '#10213f',
      textMuted: '#4f6388'
    }
  }),
  uber: buildProfile({
    slug: 'uber',
    brandName: 'Uber',
    logoUrl: 'https://storage.googleapis.com/companies-assets/uber/logo.png',
    tenantId: 'uber-1mpqh',
    dashboardApiBaseUrl: 'https://botafogo-dashboard-api-74hf2txj6q-ue.a.run.app',
    userApiBaseUrl: 'https://users.bepass.app',
    eventApiBaseUrl: 'https://tickets.bepass.app',
    portalOrigin: 'https://dashboard-uber.bepass.com.br',
    companyId: '53c83472-be76-47e0-b1ba-9023bee5175b',
    theme: {
      primary: '#000000',
      primaryContrast: '#ffffff',
      accent: '#000000',
      accentSoft: 'rgba(39, 110, 241, 0.18)',
      sidebar: '#111111',
      header: '#000000',
      surfaceAlt: '#f4f6f8',
      text: '#111827',
      textMuted: '#5f6b7a'
    }
  }),
  catarina: buildProfile({
    slug: 'catarinaaviationshow',
    brandName: 'Catarina Aviation Show',
    logoUrl: 'https://storage.googleapis.com/companies-assets/cas2026/CAS25-LOGO.jpg',
    tenantId: 'cas2026-ibmvh',
    dashboardApiBaseUrl: 'https://botafogo-dashboard-api-74hf2txj6q-ue.a.run.app',
    userApiBaseUrl: 'https://users.bepass.app',
    eventApiBaseUrl: 'https://tickets.bepass.app',
    companyId: 'dffc80b8-dac5-4bb4-9604-4bf30d6c81dc',
    theme: {
      primary: '#91cb98',
      primaryContrast: '#ffffff',
      accent: '#B3CA79',
      accentSoft: 'rgba(255, 223, 0, 0.18)',
      sidebar: '#012d99',
      header: '#013dcc',
      surfaceAlt: '#f3f7ff',
      text: '#10213f',
      textMuted: '#4f6388'
    }
  })
}

const SLUG_ALIASES: Record<string, string> = {
  botaofgo: 'botafogo',
  botafogo: 'botafogo',
  fluminense: 'fluminense',
  gremio: 'gremio',
  palmeiras: 'palmeiras',
  barueri: 'barueri',
  santos: 'santos',
  'sao-paulo': 'sao-paulo',
  saopaulo: 'sao-paulo',
  flamengo: 'flamengo',
  vasco: 'vasco',
  cbf: 'cbf',
  'cbf-game-govii': 'cbf',
  uber: 'uber',
  'uber-1mpqh': 'uber',
  catarinaaviationshow: 'catarina',
}

export const getCanonicalCompanySlug = (
  slug: string | null | undefined
): string => {
  const normalized = sanitizeCompanySlug(slug) || ''
  return SLUG_ALIASES[normalized] || normalized
}

export const getCompanyProfileBySlug = (
  slug: string | null | undefined
): CompanyProfile => {
  const canonicalSlug = getCanonicalCompanySlug(slug)
  return COMPANY_PROFILES[canonicalSlug] || COMPANY_PROFILES.palmeiras
}

export const getCompanyThemeBySlug = (
  slug: string | null | undefined
): CompanyTheme => getCompanyProfileBySlug(slug).theme
